import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { documentsApi, projectsApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, FileText, Trash2, Edit, Save, X, FolderOpen } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useClient } from '../context/ClientContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';

const DocumentsPage = () => {
  const { selectedClientId } = useClient();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    project_id: '',
  });

  const clientId = selectedClientId !== 'all' ? selectedClientId : undefined;

  useEffect(() => {
    fetchData();
  }, [selectedProject, selectedClientId]);

  const fetchData = async () => {
    try {
      const [docsRes, projectsRes] = await Promise.all([
        documentsApi.getAll(selectedProject || null, clientId),
        projectsApi.getAll(clientId),
      ]);
      setDocuments(docsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        project_id: formData.project_id || null,
      };
      await documentsApi.create(data);
      toast.success('Documento creado');
      setDialogOpen(false);
      setFormData({ title: '', content: '', project_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear documento');
    }
  };

  const handleUpdate = async () => {
    if (!selectedDoc) return;
    try {
      await documentsApi.update(selectedDoc.document_id, {
        title: selectedDoc.title,
        content: selectedDoc.content,
        project_id: selectedDoc.project_id || null,
      });
      toast.success('Documento guardado');
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar documento');
    }
  };

  const handleDelete = async (docId) => {
    try {
      await documentsApi.delete(docId);
      toast.success('Documento movido a papelera');
      if (selectedDoc?.document_id === docId) {
        setSelectedDoc(null);
      }
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Documentos">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Documentos">
      <div className="flex gap-6 h-[calc(100vh-200px)]" data-testid="documents-page">
        {/* Sidebar - Document List */}
        <div className="w-80 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <h2 className={cn('flex-1 text-sm font-medium', isDark ? 'text-zinc-300' : 'text-zinc-600')}>Documentos</h2>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="icon" className="bg-orange-500 hover:bg-orange-600" data-testid="create-doc-btn">
                  <Plus size={20} />
                </Button>
              </DialogTrigger>
              <DialogContent className={isDark ? 'bg-[#141414] border-white/10' : 'bg-white'}>
                <DialogHeader>
                  <DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Crear Documento</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Titulo</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className={isDark ? 'bg-[#1A1A1A] border-transparent text-white' : ''}
                      placeholder="Titulo del documento"
                      required
                      data-testid="doc-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Contenido</Label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className={cn('min-h-[200px]', isDark ? 'bg-[#1A1A1A] border-transparent text-white' : '')}
                      placeholder="Comienza a escribir..."
                    />
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="doc-submit">
                    Crear Documento
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Document List */}
          <div className="flex-1 overflow-y-auto space-y-2">
            {documents.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="mx-auto text-zinc-400 mb-4" size={48} />
                <p className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>Aun no hay documentos</p>
              </div>
            ) : (
              documents.map((doc) => (
                <button
                  key={doc.document_id}
                  onClick={() => {
                    setSelectedDoc(doc);
                    setEditMode(false);
                  }}
                  className={cn('w-full text-left p-4 rounded-lg transition-colors border',
                    selectedDoc?.document_id === doc.document_id
                      ? 'bg-orange-500/20 border-orange-500/30'
                      : isDark ? 'bg-[#141414] border-white/5 hover:border-white/10' : 'bg-white border-zinc-200 hover:border-zinc-300'
                  )}
                  data-testid={`doc-item-${doc.document_id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={16} className="text-orange-400" />
                    <span className={cn('font-medium truncate', isDark ? 'text-white' : 'text-zinc-900')}>{doc.title}</span>
                  </div>
                  <p className={cn('text-xs truncate', isDark ? 'text-zinc-500' : 'text-zinc-400')}>{doc.content || 'Documento vacio'}</p>
                  <p className={cn('text-xs mt-1', isDark ? 'text-zinc-600' : 'text-zinc-400')}>
                    {format(new Date(doc.updated_at), 'd MMM, yyyy', { locale: es })}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Main Content - Document Editor */}
        <Card className={cn('flex-1', isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
          <CardContent className="p-6 h-full flex flex-col">
            {selectedDoc ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  {editMode ? (
                    <Input
                      value={selectedDoc.title}
                      onChange={(e) => setSelectedDoc({ ...selectedDoc, title: e.target.value })}
                      className={cn('text-2xl font-bold bg-transparent border-b border-orange-500 rounded-none px-0 h-auto focus-visible:ring-0', isDark ? 'text-white' : 'text-zinc-900')}
                    />
                  ) : (
                    <h2 className={cn('text-2xl font-bold', isDark ? 'text-white' : 'text-zinc-900')}>{selectedDoc.title}</h2>
                  )}
                  <div className="flex items-center gap-2">
                    {editMode ? (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => setEditMode(false)} className={isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'}>
                          <X size={20} />
                        </Button>
                        <Button size="icon" onClick={handleUpdate} className="bg-green-600 hover:bg-green-700">
                          <Save size={20} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => setEditMode(true)} className={isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'}>
                          <Edit size={20} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(selectedDoc.document_id)} className="text-zinc-400 hover:text-red-400">
                          <Trash2 size={20} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-xs text-zinc-500 mb-4">
                  Ultima actualizacion: {format(new Date(selectedDoc.updated_at), "d 'de' MMMM, yyyy h:mm a", { locale: es })}
                </div>
                {editMode ? (
                  <Textarea
                    value={selectedDoc.content}
                    onChange={(e) => setSelectedDoc({ ...selectedDoc, content: e.target.value })}
                    className={cn('flex-1 resize-none', isDark ? 'bg-[#1A1A1A] border-transparent text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')}
                    placeholder="Comienza a escribir..."
                  />
                ) : (
                  <div className="flex-1 max-w-none">
                    <div className={cn('whitespace-pre-wrap', isDark ? 'text-zinc-300' : 'text-zinc-700')}>
                      {selectedDoc.content || 'Este documento esta vacio. Haz clic en editar para comenzar a escribir.'}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center">
                <FileText className="text-zinc-400 mb-4" size={64} />
                <h3 className={cn('text-xl font-medium mb-2', isDark ? 'text-white' : 'text-zinc-900')}>Selecciona un documento</h3>
                <p className={isDark ? 'text-zinc-400' : 'text-zinc-500'}>Elige un documento de la lista o crea uno nuevo</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;
